import axios from "axios";
import React, { useState, useEffect, useMemo } from "react";
import { Spinner } from "react-bootstrap";
import Modal from "react-bootstrap/Modal";
import { toast } from "react-toastify";
import { switchEndpoint } from "../../../../../../../../redux/actions/endpoints";
import { storage } from "../../../../../../../../scopedStorage";

const kg = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 3 })} kg`;

const ShowBailingModal = ({
  showBailing,
  handleCloseBailing,
  handleShowBailingSuccessful,
  bailingStat,
  weight,
  setWeight,
  category,
  setCategory,
}) => {
  const [loadingBailing, setLoadingBailing] = useState(false);
  const [loadingStock, setLoadingStock] = useState(false);
  // Loose stock the center actually holds, per sub-category.
  const [stock, setStock] = useState([]);
  // What the operator says went into the bale: { waste_category: kg }
  const [lines, setLines] = useState({});

  const token = storage.getItem("token");

  // The bale is described by its sub-categories, so the modal has to know what
  // the center is holding before the operator can split anything out of it.
  useEffect(() => {
    if (!showBailing) return;

    setLoadingStock(true);
    setLines({});
    setWeight(null);

    axios
      .get(`${switchEndpoint}/collection-center/waste-products`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        // Products and the sub-categories behind them — no balances. What the
        // center holds is never sent to the browser: an operator who can read
        // it can bail right up to it. The API checks the weight instead.
        setStock(res?.data?.data ?? res?.data ?? []);
        setLoadingStock(false);
      })
      .catch(() => {
        // A center whose composition has not been captured yet can still bail:
        // with no lines the API infers the split from the stock it holds.
        setStock([]);
        setLoadingStock(false);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showBailing]);

  const groups = useMemo(
    () => (stock || []).map((p) => ({ key: p.label, name: p.name, rows: p.categories || [] })),
    [stock]
  );

  // Only the chosen product's sub-categories can go into this bale.
  const selected = useMemo(
    () => groups.find((g) => g.key === category) || null,
    [groups, category]
  );

  // The weight bailed IS the sum of the lines — the API rejects a declared
  // split that does not add up to the total, so there is nothing to type twice.
  const totalBailed = useMemo(
    () =>
      Object.values(lines).reduce((sum, v) => {
        const n = parseFloat(v);
        return sum + (isNaN(n) ? 0 : n);
      }, 0),
    [lines]
  );

  useEffect(() => {
    setWeight(totalBailed > 0 ? totalBailed : null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalBailed]);

  const setLine = (name, value) => {
    setLines((prev) => ({ ...prev, [name]: value }));
    setShortNames([]);
  };

  // Categories the API told us are short, by raw name. Cleared as soon as the
  // operator edits anything, so a corrected split can be submitted again.
  const [shortNames, setShortNames] = useState([]);

  const isShort = (row) => shortNames.includes(row.name);

  const handleSubmit = () => {
    if (!category) {
      toast.error("Select the product being bailed");
      return;
    }
    if (!totalBailed || totalBailed <= 0) {
      toast.error("Enter the weight taken from at least one sub-category");
      return;
    }

    // Only the categories that actually contributed weight, rounded the way
    // the ledger stores them so the total still reconciles.
    const composition = Object.entries(lines)
      .map(([name, value]) => ({ name, kg: Math.round(parseFloat(value) * 1000) / 1000 }))
      .filter((line) => line.kg > 0);

    // The product is what the operator chose; the coarse pet_plastic/nylon
    // roll-up the bale record still carries is derived from it server-side, so
    // the two can no longer disagree.
    const payload = {
      total_kg: Math.round(totalBailed * 1000) / 1000,
      label: category,
      ...(composition.length ? { composition } : {}),
    };

    setLoadingBailing(true);
    axios
      .post(`${switchEndpoint}/collection-center/bail-waste`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((response) => {
        toast.success(response.data.message, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          pauseOnHover: true,
          progress: undefined,
          theme: "light",
        });
        setLoadingBailing(false);
        // Lines are reset when the modal is next opened, not here: clearing
        // them now drives the derived weight back to null, and the success
        // screen reads that weight.
        setWeight(payload.total_kg);
        handleCloseBailing();
        handleShowBailingSuccessful();
      })
      .catch((error) => {
        setLoadingBailing(false);
        const res = error?.response?.data;
        // The API answers a bad split with 422 and names the field.
        // The API names the categories that are short — never the amounts —
        // so the operator can correct the line without learning the balance.
        const short = res?.errors?.short ?? res?.short;
        setShortNames(Array.isArray(short) ? short : []);
        const detail =
          res?.errors?.composition?.[0] ||
          res?.message ||
          "Bailing failed, please try again";
        toast.error(detail, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          pauseOnHover: true,
          progress: undefined,
          theme: "light",
        });
      });
  };

  return (
    <div>
      <Modal
        show={showBailing}
        onHide={handleCloseBailing}
        backdrop="static"
        keyboard={false}
        size="lg"
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body>
          <div className="p-3">
            <div className="d-flex align-items-center">
              <div>
                <h4 className="mb-0">Bailing PET Waste</h4>
              </div>
            </div>

            <div className="w-60 newW py-3">
              <div className="form-group">
                <label htmlFor="bailingProduct">Product being bailed</label>
                <select
                  style={{ fontSize: "13px" }}
                  className="form-control form-control-lg"
                  id="bailingProduct"
                  value={category || ""}
                  onChange={(e) => {
                    setCategory(e.target.value);
                    setLines({});
                  }}
                >
                  <option value="">Select the product</option>
                  {groups.map((g) => (
                    <option key={g.key} value={g.key} style={{ fontSize: "13px" }}>
                      {g.name}
                    </option>
                  ))}
                </select>
                <small style={{ fontSize: "11px", color: "#6C7A72" }}>
                  One bale is one product. Split the weight across the
                  sub-categories that went into it.
                </small>
              </div>
            </div>

            <div className="mb-2 d-flex justify-content-between align-items-center">
              <h4 className="mb-0" style={{ fontSize: "14px" }}>
                What went into the bale
              </h4>
              <span style={{ fontSize: "12px", color: "#6C7A72" }}>
                Enter what went into this bale
              </span>
            </div>

            {loadingStock ? (
              <div className="py-3 text-center">
                <Spinner animation="border" size="sm" role="status" />
              </div>
            ) : !selected ? (
              <p style={{ fontSize: "13px", color: "#6C7A72" }}>
                Choose the product above to see the sub-categories behind it.
              </p>
            ) : stock.length === 0 ? (
              <p style={{ fontSize: "13px", color: "#6C7A72" }}>
                This center has no loose stock broken down by sub-category yet.
                Enter the weight below and the split will be worked out from the
                stock it holds.
              </p>
            ) : (
              <div className="table-responsive" style={{ maxHeight: "260px", overflowY: "auto" }}>
                <table className="table table-sm" style={{ fontSize: "13px" }}>
                  <thead>
                    <tr>
                      <th>Sub-category</th>
                      <th className="text-right" style={{ width: "150px" }}>Weight bailed</th>
                    </tr>
                  </thead>
                  <tbody>
                    {selected.rows.map((row) => {
                      const short = isShort(row);
                      return (
                        <tr key={row.name}>
                          <td style={{ paddingLeft: "12px", color: short ? "#B4532A" : undefined }}>
                            {row.label || row.name}
                            {short ? (
                              <span className="ml-1" style={{ fontSize: "11px", color: "#B4532A" }}>
                                more than this center holds
                              </span>
                            ) : null}
                          </td>
                          <td className="text-right">
                            <input
                              type="number"
                              min="0"
                              step="0.001"
                              className="form-control form-control-sm text-right"
                              style={{ fontSize: "13px", borderColor: short ? "#B4532A" : undefined }}
                              placeholder="0"
                              value={lines[row.name] ?? ""}
                              onChange={(e) => setLine(row.name, e.target.value)}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr>
                      <th>Total bailed</th>
                      <th className="text-right">{kg(totalBailed)}</th>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}

            {stock.length === 0 ? (
              <div className="w-60 newW pt-1">
                <div className="form-group">
                  <label className="label-control mb-0 labelStyle">Weight Bailed</label>
                  <input
                    type="number"
                    min="0"
                    step="0.001"
                    style={{ fontSize: "13px" }}
                    className="form-control form-control-lg mt-1 inputStyle"
                    placeholder="Enter weight bailed"
                    value={weight ?? ""}
                    onChange={(e) => setWeight(e.target.value)}
                  />
                </div>
              </div>
            ) : null}

            {shortNames.length ? (
              <p style={{ fontSize: "12px", color: "#B4532A" }}>
                Reduce the highlighted {shortNames.length === 1 ? "line" : "lines"} — this
                center does not hold that much. Change any weight to try again.
              </p>
            ) : null}

            <div className="pt-2 w-25 adjW">
              <button
                type="button"
                className="btn loginBtn btn-sm btn-block"
                onClick={handleSubmit}
                disabled={loadingBailing || loadingStock || !selected || totalBailed <= 0 || shortNames.length > 0}
              >
                {loadingBailing ? (
                  <div className="" style={{ margin: "0 auto" }}>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" />
                  </div>
                ) : (
                  "Confirm"
                )}
              </button>
            </div>
          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ShowBailingModal;
